/**
 * ``useClassification`` — the single choke point the classification badge
 * reads its value from (REQ-UI-058).
 *
 * # MVP behaviour
 *
 * Returns the deployment-wide classification (:func:`useDeploymentConfig`) —
 * one marking for the whole UI, sourced from ``GET /api/config``.
 *
 * # POST-MVP seam (REQ-UI-058 / ADR-024 Open Item 4)
 *
 * REQ-UI-058 ultimately requires the marking to reflect the **most-restrictive**
 * classification across the *currently-visible panels* — opening a higher-
 * classification panel raises it; closing it lowers it. That per-panel reporting
 * is explicitly post-MVP (ADR-024 Open Item 4). When it lands, THIS hook becomes
 * the place that combines the deployment floor with the max of the visible
 * panels' classifications. Routing every consumer through this one hook keeps
 * that future change local — the :class:`ClassificationBadge` need not change.
 *
 * Cross-references:
 *   - REQ-UI-058 (classification marking; per-panel max is post-MVP).
 *   - ADR-024 Open Item 4 (per-panel classification scope — deferred).
 *   - chrome/use-deployment-config.ts (the MVP source).
 *   - chrome/classification-badge.tsx (the consumer).
 */
import type { ResolvedClassification } from "@/chrome/classification-badge";
import { useDeploymentConfig } from "@/chrome/use-deployment-config";

/**
 * Resolve the classification the badge should display.
 *
 * @returns The resolved ``{ level, text }``, or ``null`` for the fail-safe
 *     UNVERIFIED state (pending / error).
 */
export function useClassification(): ResolvedClassification | null {
  // MVP: deployment-wide marking. Post-MVP: combine with the max
  // classification across visible panels (see module docstring §seam).
  return useDeploymentConfig()?.classification ?? null;
}
